/**
 * FH Hero Component System
 * 
 * OOP Architecture:
 * - FHHeroComponent (Base): Core component initialization & lifecycle
 * - FHSliderController: Slide navigation and autoplay logic
 * - FHScheduleManager: Slide visibility scheduling
 * - FHHotspotManager: Product hotspot interactions
 * - FHVideoController: Video playback management
 * 
 * Design Principles:
 * - Single Responsibility: Each class handles one concern
 * - Open/Closed: Extendable via configuration, not modification
 * - Dependency Injection: Controllers receive container reference
 * - Encapsulation: Private state via # prefix
 */

// =============================================================================
// BASE COMPONENT
// =============================================================================

class FHHeroComponent {
  /** @type {HTMLElement} */
  #container;
  /** @type {Object} */
  #config;
  /** @type {FHSliderController|null} */
  #sliderController = null;
  /** @type {FHHotspotManager|null} */
  #hotspotManager = null;

  /**
   * @param {HTMLElement} container - The hero section element
   */
  constructor(container) {
    this.#container = container;
    this.#config = this.#parseConfig();
    this.#initialize();
  }

  /**
   * Parse configuration from data attributes
   * @returns {Object} Configuration object
   */
  #parseConfig() {
    const dataset = this.#container.dataset;
    return {
      mode: dataset.mode || 'static',
      autoplay: dataset.autoplay === 'true',
      autoplaySpeed: parseInt(dataset.autoplaySpeed) || 5000,
      loop: dataset.loop === 'true',
      sectionId: dataset.sectionId
    };
  }

  /**
   * Initialize component based on mode
   */
  #initialize() {
    // Initialize hotspots for all modes
    this.#hotspotManager = new FHHotspotManager(this.#container);

    // Initialize slider only in slider mode
    if (this.#config.mode === 'slider') {
      const sliderElement = this.#container.querySelector('[data-hero-slider]');
      if (sliderElement) {
        this.#sliderController = new FHSliderController(
          this.#container,
          sliderElement,
          this.#config
        );
      }
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.#sliderController?.destroy();
    this.#hotspotManager?.destroy();
  }

  /**
   * Get current configuration
   * @returns {Object}
   */
  getConfig() {
    return { ...this.#config };
  }
}

// =============================================================================
// SLIDER CONTROLLER
// =============================================================================

class FHSliderController {
  /** @type {HTMLElement} */
  #container;
  /** @type {HTMLElement} */
  #slider;
  /** @type {Object} */
  #config;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #visibleSlides = [];
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number|null} */
  #autoplayInterval = null;
  /** @type {FHScheduleManager} */
  #scheduleManager;
  /** @type {FHVideoController} */
  #videoController;
  /** @type {AbortController} */
  #abortController;

  /**
   * @param {HTMLElement} container - Parent container
   * @param {HTMLElement} slider - Slider element
   * @param {Object} config - Configuration
   */
  constructor(container, slider, config) {
    this.#container = container;
    this.#slider = slider;
    this.#config = config;
    this.#abortController = new AbortController();
    
    this.#initializeSlides();
    this.#setupControllers();
    this.#bindEvents();
    this.#start();
  }

  /**
   * Initialize slide elements
   */
  #initializeSlides() {
    const slidesContainer = this.#slider.querySelector('[data-hero-slides]');
    if (!slidesContainer) return;

    this.#slides = Array.from(
      slidesContainer.querySelectorAll('.fh-hero__slide, [data-fh-slide]')
    );

    // Assign indices and set initial state
    this.#slides.forEach((slide, index) => {
      slide.dataset.slideIndex = index;
      slide.classList.toggle('is-active', index === 0);
    });
  }

  /**
   * Setup sub-controllers
   */
  #setupControllers() {
    this.#scheduleManager = new FHScheduleManager(this.#slides, () => {
      this.#updateVisibleSlides();
    });

    this.#videoController = new FHVideoController(this.#slides);
  }

  /**
   * Bind event listeners
   */
  #bindEvents() {
    const signal = this.#abortController.signal;

    // Navigation buttons
    const prevBtn = this.#container.querySelector('[data-hero-prev]');
    const nextBtn = this.#container.querySelector('[data-hero-next]');

    prevBtn?.addEventListener('click', () => this.#navigate(-1), { signal });
    nextBtn?.addEventListener('click', () => this.#navigate(1), { signal });

    // Touch/swipe support
    this.#setupSwipeHandling(signal);

    // Dots navigation
    this.#setupDotsNavigation(signal);
  }

  /**
   * Setup swipe gesture handling
   * @param {AbortSignal} signal
   */
  #setupSwipeHandling(signal) {
    const slidesContainer = this.#slider.querySelector('[data-hero-slides]');
    if (!slidesContainer) return;

    let touchStartX = 0;
    const SWIPE_THRESHOLD = 50;

    slidesContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true, signal });

    slidesContainer.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        this.#navigate(diff > 0 ? 1 : -1);
      }
    }, { passive: true, signal });
  }

  /**
   * Setup dots navigation
   * @param {AbortSignal} signal
   */
  #setupDotsNavigation(signal) {
    const dotsContainer = this.#container.querySelector('[data-hero-dots]');
    if (!dotsContainer) return;

    dotsContainer.addEventListener('click', (e) => {
      const dot = e.target.closest('.fh-hero__dot');
      if (dot) {
        const index = parseInt(dot.dataset.dotIndex);
        this.goToSlide(index);
        this.#resetAutoplay();
      }
    }, { signal });
  }

  /**
   * Start the slider
   */
  #start() {
    this.#updateVisibleSlides();
    this.goToSlide(0);

    if (this.#config.autoplay && this.#visibleSlides.length > 1) {
      this.#startAutoplay();
    }
  }

  /**
   * Update visible slides based on scheduling
   */
  #updateVisibleSlides() {
    this.#visibleSlides = this.#slides.filter(
      slide => !slide.classList.contains('is-hidden-scheduled')
    );

    this.#updateDots();

    // Handle current slide becoming hidden
    const currentSlide = this.#slides[this.#currentIndex];
    if (currentSlide?.classList.contains('is-hidden-scheduled')) {
      this.goToSlide(0);
    }

    // Update autoplay based on slide count
    if (this.#visibleSlides.length <= 1) {
      this.#stopAutoplay();
    } else if (this.#config.autoplay && !this.#autoplayInterval) {
      this.#startAutoplay();
    }
  }

  /**
   * Update dots UI
   */
  #updateDots() {
    const dotsContainer = this.#container.querySelector('[data-hero-dots]');
    if (!dotsContainer) return;

    dotsContainer.innerHTML = '';

    this.#visibleSlides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = `fh-hero__dot${index === this.#currentIndex ? ' is-active' : ''}`;
      dot.dataset.dotIndex = index;
      dot.setAttribute('aria-label', `Slide ${index + 1} von ${this.#visibleSlides.length}`);
      dotsContainer.appendChild(dot);
    });
  }

  /**
   * Navigate by direction
   * @param {number} direction - -1 for previous, 1 for next
   */
  #navigate(direction) {
    this.goToSlide(this.#currentIndex + direction);
    this.#resetAutoplay();
  }

  /**
   * Go to specific slide
   * @param {number} index - Target slide index
   */
  goToSlide(index) {
    if (this.#visibleSlides.length === 0) return;

    // Handle bounds with loop option
    if (index < 0) {
      index = this.#config.loop ? this.#visibleSlides.length - 1 : 0;
    } else if (index >= this.#visibleSlides.length) {
      index = this.#config.loop ? 0 : this.#visibleSlides.length - 1;
    }

    this.#currentIndex = index;

    // Update slide visibility
    this.#slides.forEach(slide => slide.classList.remove('is-active'));
    this.#visibleSlides[index]?.classList.add('is-active');

    // Update dots
    const dots = this.#container.querySelectorAll('.fh-hero__dot');
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

    // Handle video playback
    this.#videoController.onSlideChange(this.#visibleSlides[index]);
  }

  /**
   * Start autoplay
   */
  #startAutoplay() {
    if (this.#autoplayInterval) return;

    this.#autoplayInterval = setInterval(() => {
      this.#navigate(1);
    }, this.#config.autoplaySpeed);
  }

  /**
   * Stop autoplay
   */
  #stopAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  /**
   * Reset autoplay timer
   */
  #resetAutoplay() {
    this.#stopAutoplay();
    if (this.#config.autoplay && this.#visibleSlides.length > 1) {
      this.#startAutoplay();
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.#stopAutoplay();
    this.#scheduleManager.destroy();
    this.#abortController.abort();
  }
}

// =============================================================================
// SCHEDULE MANAGER
// =============================================================================

class FHScheduleManager {
  /** @type {HTMLElement[]} */
  #slides;
  /** @type {Function} */
  #onUpdate;
  /** @type {number|null} */
  #checkInterval = null;

  static CHECK_INTERVAL_MS = 60000;

  /**
   * @param {HTMLElement[]} slides - Slide elements
   * @param {Function} onUpdate - Callback when visibility changes
   */
  constructor(slides, onUpdate) {
    this.#slides = slides;
    this.#onUpdate = onUpdate;
    this.#startScheduleChecks();
  }

  /**
   * Start periodic schedule checks
   */
  #startScheduleChecks() {
    this.#checkSchedules();
    this.#checkInterval = setInterval(() => {
      this.#checkSchedules();
    }, FHScheduleManager.CHECK_INTERVAL_MS);
  }

  /**
   * Check all slides for schedule visibility
   */
  #checkSchedules() {
    const now = Date.now() / 1000; // Unix timestamp
    let changed = false;

    this.#slides.forEach(slide => {
      const wasVisible = !slide.classList.contains('is-hidden-scheduled');
      const isVisible = this.#isSlideVisible(slide, now);

      if (wasVisible !== isVisible) {
        slide.classList.toggle('is-hidden-scheduled', !isVisible);
        changed = true;
      }
    });

    if (changed) {
      this.#onUpdate();
    }
  }

  /**
   * Check if slide is visible based on schedule
   * @param {HTMLElement} slide
   * @param {number} now - Current timestamp
   * @returns {boolean}
   */
  #isSlideVisible(slide, now) {
    const scheduleStart = slide.dataset.scheduleStart;
    const scheduleEnd = slide.dataset.scheduleEnd;

    // No scheduling = always visible
    if (!scheduleStart && !scheduleEnd) return true;

    // Check start time
    if (scheduleStart && now < parseInt(scheduleStart)) {
      return false;
    }

    // Check end time
    if (scheduleEnd && now > parseInt(scheduleEnd)) {
      return false;
    }

    return true;
  }

  /**
   * Clean up
   */
  destroy() {
    if (this.#checkInterval) {
      clearInterval(this.#checkInterval);
      this.#checkInterval = null;
    }
  }
}

// =============================================================================
// VIDEO CONTROLLER
// =============================================================================

class FHVideoController {
  /** @type {HTMLElement[]} */
  #slides;

  /**
   * @param {HTMLElement[]} slides
   */
  constructor(slides) {
    this.#slides = slides;
  }

  /**
   * Handle slide change - pause inactive, play active
   * @param {HTMLElement|null} activeSlide
   */
  onSlideChange(activeSlide) {
    // Pause all videos
    this.#slides.forEach(slide => {
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    });

    // Play videos in active slide
    if (activeSlide) {
      const videos = activeSlide.querySelectorAll('video');
      videos.forEach(video => {
        video.play().catch(() => {
          // Autoplay blocked - silent fail
        });
      });
    }
  }
}

// =============================================================================
// HOTSPOT MANAGER
// =============================================================================

class FHHotspotManager {
  /** @type {HTMLElement} */
  #container;
  /** @type {HTMLElement[]} */
  #hotspots = [];
  /** @type {AbortController} */
  #abortController;

  static BODY_SCROLL_LOCK_CLASS = 'fh-hotspot-open';

  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.#container = container;
    this.#abortController = new AbortController();
    this.#initializeHotspots();
  }

  /**
   * Initialize hotspot elements and events
   */
  #initializeHotspots() {
    this.#hotspots = Array.from(
      this.#container.querySelectorAll('.fh-hero__hotspot')
    );

    if (this.#hotspots.length === 0) return;

    const signal = this.#abortController.signal;

    // Setup individual hotspot events
    this.#hotspots.forEach(hotspot => {
      this.#setupHotspotEvents(hotspot, signal);
    });

    // Global close handlers
    this.#setupGlobalHandlers(signal);
  }

  /**
   * Setup events for a single hotspot
   * @param {HTMLElement} hotspot
   * @param {AbortSignal} signal
   */
  #setupHotspotEvents(hotspot, signal) {
    const trigger = hotspot.querySelector('.fh-hero__hotspot-trigger');
    const closeBtn = hotspot.querySelector('[data-hotspot-close]');

    // Toggle on trigger click
    trigger?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#toggleHotspot(hotspot);
    }, { signal });

    // Close button
    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#closeHotspot(hotspot);
    }, { signal });
  }

  /**
   * Setup global event handlers
   * @param {AbortSignal} signal
   */
  #setupGlobalHandlers(signal) {
    // Escape key closes hotspots
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeAllHotspots();
      }
    }, { signal });

    // Click outside closes hotspots
    document.addEventListener('click', (e) => {
      const openHotspot = this.#container.querySelector('.fh-hero__hotspot.is-open');
      if (openHotspot && 
          !e.target.closest('.fh-hero__hotspot-popup') && 
          !e.target.closest('.fh-hero__hotspot-trigger')) {
        this.#closeAllHotspots();
      }
    }, { signal });
  }

  /**
   * Toggle hotspot open/closed
   * @param {HTMLElement} hotspot
   */
  #toggleHotspot(hotspot) {
    const isOpening = !hotspot.classList.contains('is-open');

    // Close others first
    this.#hotspots.forEach(h => {
      if (h !== hotspot) h.classList.remove('is-open');
    });

    // Toggle this one
    hotspot.classList.toggle('is-open', isOpening);
    
    // Update body scroll
    document.body.style.overflow = isOpening ? 'hidden' : '';
  }

  /**
   * Close specific hotspot
   * @param {HTMLElement} hotspot
   */
  #closeHotspot(hotspot) {
    hotspot.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /**
   * Close all hotspots
   */
  #closeAllHotspots() {
    this.#hotspots.forEach(h => h.classList.remove('is-open'));
    document.body.style.overflow = '';
  }

  /**
   * Clean up
   */
  destroy() {
    this.#closeAllHotspots();
    this.#abortController.abort();
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Component Registry - Manages all FH Hero instances
 */
const FHHeroRegistry = {
  /** @type {Map<string, FHHeroComponent>} */
  instances: new Map(),

  /**
   * Initialize a hero section
   * @param {HTMLElement} element
   * @returns {FHHeroComponent}
   */
  init(element) {
    const id = element.dataset.sectionId || element.id;
    
    // Destroy existing instance if any
    this.destroy(id);

    const instance = new FHHeroComponent(element);
    this.instances.set(id, instance);
    return instance;
  },

  /**
   * Destroy a hero instance
   * @param {string} id
   */
  destroy(id) {
    const instance = this.instances.get(id);
    if (instance) {
      instance.destroy();
      this.instances.delete(id);
    }
  },

  /**
   * Initialize all hero sections on page
   */
  initAll() {
    document.querySelectorAll('[data-section-type="fh-hero"]').forEach(element => {
      this.init(element);
    });
  }
};

// DOM Ready initialization
document.addEventListener('DOMContentLoaded', () => {
  FHHeroRegistry.initAll();
});

// Shopify Theme Editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="fh-hero"]');
    if (section) {
      FHHeroRegistry.init(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-section-type="fh-hero"]');
    if (section) {
      FHHeroRegistry.destroy(section.dataset.sectionId || section.id);
    }
  });
}
